/**
 * The Finder-style Files page.
 *
 * Three panes: a sidebar of mounts, a main column (breadcrumbs + toolbar + a
 * sortable list of the current directory), and a preview pane. Selection and
 * location both live in the URL — `?dir=` is the browsed folder and `?path=` is
 * the selected file — so a shared link opens the same view, per the project's
 * deep-linking rule.
 *
 * The chat drawer and the account Files tab keep the older FileTreeComponent:
 * those hosts want a compact picker, not a file manager. This page is the one
 * that gets the Finder treatment.
 */

import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type VfsFileEntry } from '../shared/api-client';
import { BreadcrumbsComponent } from './BreadcrumbsComponent';
import { FileIconComponent } from './FileIconComponent';
import { FileListComponent } from './FileListComponent';
import { FilePreviewComponent } from './FilePreviewComponent';
import { FileRowMenuComponent, downloadVfsEntry } from './FileRowMenuComponent';
import { FilesSidebarComponent, mountLabel } from './FilesSidebarComponent';
import { FilesToolbarComponent, type UploadProgressItem } from './FilesToolbarComponent';
import { FolderPreviewComponent } from './FolderPreviewComponent';
import { useVfsBrowser } from './use-vfs-browser';
import './FilesBrowserPage.css';

function basename(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx === -1 ? path : path.slice(idx + 1);
}
function parentOf(path: string): string {
  const idx = path.lastIndexOf('/');
  return idx <= 0 ? '/' : path.slice(0, idx);
}

/** True when `dir` is inside a writable mount and not inside a synced folder. */
function dirIsWritable(
  dir: string | undefined,
  writableRoots: string[],
  syncSourcePaths: Set<string>,
): boolean {
  if (!dir) return false;
  if (!writableRoots.some((m) => dir === m || dir.startsWith(`${m}/`))) return false;
  for (const frozen of syncSourcePaths) {
    if (dir === frozen || dir.startsWith(`${frozen}/`)) return false;
  }
  return true;
}

export function FilesBrowserPage(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const dirParam = searchParams.get('dir') ?? undefined;
  const pathParam = searchParams.get('path') ?? undefined;

  // Library is always mounted for a signed-in user on the standalone page, so it
  // is the honest default before the URL says otherwise.
  const dir = dirParam ?? '/library';

  const browser = useVfsBrowser(dir);
  const { mounts, syncSources, entries, loading, error, setError, refresh } = browser;

  const writableRoots = useMemo(() => mounts.filter((m) => m.writable).map((m) => m.path), [mounts]);
  const syncSourcePaths = useMemo(() => new Set(syncSources.map((s) => s.path)), [syncSources]);
  const mountLabels = useMemo(() => {
    const out: Record<string, string> = {};
    for (const m of mounts) out[m.path] = mountLabel(m.path);
    return out;
  }, [mounts]);

  // First load with a `?path=` but no `?dir=`: point the browser at the file's
  // parent so the list shows its siblings, which is what Finder does.
  useEffect(() => {
    if (pathParam && !dirParam) {
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('dir', parentOf(pathParam)); return next; }, { replace: true });
    }
  }, [pathParam, dirParam, setSearchParams]);

  const [creatingFolder, setCreatingFolder] = useState(false);
  const [pendingFolderName, setPendingFolderName] = useState('');
  const [uploads, setUploads] = useState<UploadProgressItem[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [menu, setMenu] = useState<{ entry: VfsFileEntry; anchor: HTMLElement } | null>(null);
  const [renameTarget, setRenameTarget] = useState<string | null>(null);

  // Multi-select lives in the page because drag and the row menu both need it.
  // Cleared whenever the browser navigates to a new folder, so a stale
  // selection from the previous directory never follows the user into a new one.
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  // The anchor for shift-click range selection — the last row a plain or
  // cmd-click landed on. null means shift will behave like a plain click.
  const [anchorPath, setAnchorPath] = useState<string | null>(null);
  useEffect(() => { setSelectedPaths(new Set()); setAnchorPath(null); }, [dir]);

  // The single selected entry for the preview pane. A file is reflected in
  // `?path=` (so deep links restore it); a folder is not, because a folder has
  // no contents to preview and putting it in the URL would imply a file. Resolve
  // both: a file from the URL, a single-selected folder from selectedPaths.
  const selected: VfsFileEntry | null = useMemo(() => {
    if (pathParam) return entries.find((e) => e.path === pathParam) ?? null;
    if (selectedPaths.size === 1) {
      const p = [...selectedPaths][0]!;
      return entries.find((e) => e.path === p) ?? null;
    }
    return null;
  }, [entries, pathParam, selectedPaths]);

  // Restore a deep-linked single selection (?path=) into the set on load / when
  // the listing refreshes, so the URL-selected file is visibly selected.
  useEffect(() => {
    if (pathParam && entries.some((e) => e.path === pathParam)) {
      setSelectedPaths((prev) => prev.has(pathParam) ? prev : new Set([pathParam]));
    } else if (!pathParam && selectedPaths.size === 1) {
      // URL cleared (e.g. after delete) — drop a lone selection.
      setSelectedPaths(new Set());
    }
  }, [pathParam, entries, selectedPaths.size]);

  const canWrite = dirIsWritable(dir, writableRoots, syncSourcePaths);

  const navigate = useCallback((toDir: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('dir', toDir);
      next.delete('path');
      return next;
    });
  }, [setSearchParams]);

  // One click with its modifier state, resolved into a selection update.
  // Plain = replace with this row; cmd/ctrl = toggle this row; shift = range
  // from the anchor to this row in the currently-sorted listing. Directories
  // are selectable too — selecting a folder highlights it without previewing it.
  const selectClick = useCallback((entry: VfsFileEntry, e: { metaKey: boolean; ctrlKey: boolean; shiftKey: boolean }) => {
    const sortedPaths = (function () {
      const dirs = entries.filter((x) => x.kind === 'directory').sort((a, b) => a.name.localeCompare(b.name));
      const files = entries.filter((x) => x.kind === 'file').sort((a, b) => a.name.localeCompare(b.name));
      return [...dirs, ...files].map((x) => x.path);
    })();
    setSelectedPaths((prev) => {
      if (e.shiftKey && anchorPath !== null) {
        const a = sortedPaths.indexOf(anchorPath);
        const b = sortedPaths.indexOf(entry.path);
        if (a === -1 || b === -1) return new Set([entry.path]);
        const [lo, hi] = a < b ? [a, b] : [b, a];
        return new Set(sortedPaths.slice(lo, hi + 1));
      }
      if (e.metaKey || e.ctrlKey) {
        const next = new Set(prev);
        if (next.has(entry.path)) next.delete(entry.path); else next.add(entry.path);
        return next;
      }
      return new Set([entry.path]);
    });
    if (!e.shiftKey && !e.metaKey && !e.ctrlKey) setAnchorPath(entry.path);
    // Only touch the URL for a plain single-file selection — directories and
    // multi-selections have nothing to preview, and a setSearchParams here on a
    // directory click races the navigate() a following dblclick issues, which
    // is how double-click-to-open-folder broke. The URL is a deep-link for the
    // previewed file, not a selection mirror.
    if (entry.kind === 'file' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      setSearchParams((prev) => {
        const wantPath = entry.path;
        const hasPath = prev.get('path') ?? null;
        if (wantPath === hasPath) return prev;
        const next = new URLSearchParams(prev);
        next.set('path', wantPath);
        return next;
      });
    }
  }, [entries, anchorPath, setSearchParams]);

  // Convenience kept for callers that mean "select just this file" (the row
  // menu's rename entry) — equivalent to a plain click on the row.
  const select = useCallback((entry: VfsFileEntry) => {
    selectClick(entry, { metaKey: false, ctrlKey: false, shiftKey: false });
  }, [selectClick]);

  const open = useCallback((entry: VfsFileEntry) => {
    if (entry.kind === 'directory') { navigate(entry.path); return; }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('dir', parentOf(entry.path));
      next.set('path', entry.path);
      return next;
    });
  }, [navigate, setSearchParams]);

  const handlePickFiles = useCallback(async (files: FileList) => {
    if (!canWrite) return;
    const items: UploadProgressItem[] = Array.from(files).map((f) => ({
      id: `${f.name}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: f.name,
      state: 'uploading',
    }));
    setUploads((prev) => [...prev, ...items]);
    for (let i = 0; i < files.length; i++) {
      const file = files[i]!;
      const item = items[i]!;
      const res = await api.uploadVfsFile(file, dir);
      setUploads((prev) => prev.map((u) => u.id === item.id
        ? { ...u, state: res.success ? 'done' : 'error', error: res.success ? undefined : res.error.message }
        : u));
    }
    await refresh();
    // Clear completed uploads after a short beat so the toolbar does not hold a
    // stale list, but leave errors visible long enough to read.
    setTimeout(() => setUploads((prev) => prev.filter((u) => u.state === 'error')), 2500);
  }, [canWrite, dir, refresh]);

  const commitFolder = useCallback(async () => {
    const name = pendingFolderName.trim();
    if (!name || !canWrite) { setCreatingFolder(false); setPendingFolderName(''); return; }
    const res = await api.createVfsFolder({ path: `${dir.replace(/\/+$/, '')}/${name}` });
    setCreatingFolder(false);
    setPendingFolderName('');
    if (res.success) await refresh();
  }, [pendingFolderName, canWrite, dir, refresh]);

  const cancelFolder = useCallback(() => { setCreatingFolder(false); setPendingFolderName(''); }, []);

  const rename = useCallback(async (path: string, newName: string): Promise<boolean> => {
    const name = newName.trim();
    if (!name || name === basename(path)) return true;
    const res = await api.renameVfsEntry({ path, name });
    if (!res.success) return false;
    await refresh();
    if (selected?.path === path) {
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('path', res.data.path); return next; });
    }
    return true;
  }, [refresh, selected?.path, setSearchParams]);

  // Move a set of paths into a folder. Each is renamed to <toDir>/<basename>;
  // a name that already exists at the destination is skipped with an inline
  // error rather than clobbered — the same stance as the single-file move, but
  // batched so a multi-select drag does not silently drop half its files.
  const dropMove = useCallback(async (fromPaths: string[], toDir: string) => {
    const cleaned = fromPaths.filter((p) => {
      if (p === toDir || p.startsWith(`${toDir}/`)) return false; // can't move a folder into itself
      return parentOf(p) !== toDir; // already there
    });
    if (cleaned.length === 0) return;
    let firstError: string | null = null;
    let movedCount = 0;
    for (const fromPath of cleaned) {
      const to = `${toDir.replace(/\/+$/, '')}/${basename(fromPath)}`;
      if (to === fromPath) continue;
      const res = await api.moveVfsEntry({ from: fromPath, to });
      if (res.success) movedCount++;
      else if (firstError === null) firstError = res.error.message;
    }
    await refresh();
    setSelectedPaths(new Set());
    if (firstError !== null && movedCount === 0) {
      setMenu(null);
      // Surface the failure inline via the browser error slot.
      setError(firstError);
    }
  }, [refresh]);

  // Delete every selected entry. Used by the row menu when more than one row
  // is selected; the menu's confirm text names the count.
  const handleDelete = useCallback(async (entries: VfsFileEntry[]) => {
    for (const entry of entries) {
      await api.deleteVfsEntry({ path: entry.path, kind: entry.kind });
    }
    await refresh();
    setSelectedPaths(new Set());
    setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('path'); return next; });
  }, [refresh, setSearchParams]);

  // Move-to candidates: writable mount roots + the current directory's writable
  // subfolders. The row menu component filters out self/descendants.
  const moveTargets = useMemo(() => {
    const out: Array<{ path: string; name: string }> = [];
    for (const root of writableRoots) out.push({ path: root, name: mountLabel(root) });
    for (const e of entries) {
      if (e.kind !== 'directory') continue;
      if (!dirIsWritable(e.path, writableRoots, syncSourcePaths)) continue;
      out.push({ path: e.path, name: e.name });
    }
    return out;
  }, [writableRoots, entries, syncSourcePaths]);

  const startRenameFromMenu = useCallback((entry: VfsFileEntry) => {
    // Select the row so it is visibly the rename subject, then ask the list to
    // open its inline rename input for this path.
    select(entry);
    setRenameTarget(entry.path);
  }, [select]);

  return (
    <div
      className={`files-browser${dragActive ? ' files-browser--drag-active' : ''}`}
      onDragOver={(e) => { if (canWrite && e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
      onDrop={(e) => {
        if (canWrite && e.dataTransfer.files.length > 0) {
          e.preventDefault();
          void handlePickFiles(e.dataTransfer.files);
        }
      }}
    >
      <FilesSidebarComponent
        mounts={mounts}
        syncSourcePaths={syncSourcePaths}
        currentDir={dir}
        onNavigate={navigate}
        onDropMove={dropMove}
        dragActive={dragActive}
      />

      <section className="files-browser-main">
        <BreadcrumbsComponent
          dir={dir}
          mountLabels={mountLabels}
          onNavigate={navigate}
          onDropMove={dropMove}
          dragActive={dragActive}
        />
        <FilesToolbarComponent
          canWrite={canWrite}
          isCreatingFolder={creatingFolder}
          canGoUp={dir.split('/').filter(Boolean).length > 1}
          onGoUp={() => navigate(parentOf(dir))}
          onStartNewFolder={() => { setCreatingFolder(true); setPendingFolderName(''); }}
          onCancelNewFolder={cancelFolder}
          onPickFiles={handlePickFiles}
          uploads={uploads}
        />
        {error && <div className="files-browser-error" role="status">{error}</div>}
        {loading && entries.length === 0 && !creatingFolder && (
          <div className="files-browser-loading">Loading…</div>
        )}
        <FileListComponent
          entries={entries}
          selectedPaths={selectedPaths}
          writableRoots={writableRoots}
          syncSourcePaths={syncSourcePaths}
          creatingFolder={creatingFolder}
          pendingFolderName={pendingFolderName}
          onFolderNameChange={setPendingFolderName}
          onCommitFolder={commitFolder}
          onCancelFolder={cancelFolder}
          onRename={rename}
          renameTarget={renameTarget}
          onRenameTargetClear={() => setRenameTarget(null)}
          onSelectClick={selectClick}
          onOpen={open}
          onNavigate={navigate}
          onDropMove={dropMove}
          onRowMenu={(entry, anchor) => setMenu({ entry, anchor })}
          onDragStateChange={setDragActive}
        />
      </section>

      <section className="files-browser-preview">
        {selectedPaths.size > 1 ? (
          <div className="files-browser-preview-empty">
            <FileIconComponent kind="generic" size={32} />
            <p>{selectedPaths.size} files selected.</p>
            <p className="files-browser-preview-hint">Drag them into a folder, or use the ⋯ menu to delete.</p>
          </div>
        ) : selected ? (
          selected.kind === 'directory'
            ? <FolderPreviewComponent entry={selected} />
            : <FilePreviewComponent entry={selected} />
        ) : (
          <div className="files-browser-preview-empty">
            <FileIconComponent kind="generic" size={32} />
            <p>Select a file to preview it, or double-click a folder to open it.</p>
          </div>
        )}
      </section>

      {menu && (
        <FileRowMenuComponent
          entry={menu.entry}
          selectedEntries={entries.filter((e) => selectedPaths.has(e.path))}
          anchor={menu.anchor}
          currentDir={dir}
          moveTargets={moveTargets}
          onRename={(entry) => { startRenameFromMenu(entry); }}
          onDownload={(entry) => downloadVfsEntry(entry)}
          onMoveTo={(entry, toDir) => { void dropMove([entry.path], toDir); }}
          onDelete={handleDelete}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}
